import type { EmojiJsonItem } from './emoji-data';
import { emoji } from './emoji-data';
import { materialSymbols } from './material-symbols';
import type { EmojiItem, EmojiSkinVariation, SkinColour, SymbolGroup, SymbolItem } from './types';

export enum EmojiCategory {
  RecentlyUsed = 'app.emoji.category.recently_used',
  SmileysEmotion = 'app.emoji.category.smileys_emotion',
  PeopleBody = 'app.emoji.category.people_body',
  AnimalsNature = 'app.emoji.category.animals_nature',
  FoodDrink = 'app.emoji.category.food_drink',
  TravelPlaces = 'app.emoji.category.travel_places',
  Activities = 'app.emoji.category.activities',
  Objects = 'app.emoji.category.objects',
  Symbols = 'app.emoji.category.symbols',
  Flags = 'app.emoji.category.flags',
  SkinTones = 'app.emoji.category.skin_tones',
}

const categoryMap: Record<string, EmojiCategory> = {
  'Smileys & Emotion': EmojiCategory.SmileysEmotion,
  'People & Body': EmojiCategory.PeopleBody,
  'Animals & Nature': EmojiCategory.AnimalsNature,
  'Food & Drink': EmojiCategory.FoodDrink,
  'Travel & Places': EmojiCategory.TravelPlaces,
  Activities: EmojiCategory.Activities,
  Objects: EmojiCategory.Objects,
  Symbols: EmojiCategory.Symbols,
  Flags: EmojiCategory.Flags,
  Component: EmojiCategory.SkinTones,
};

export const unifiedToEmoji = (unicode: string): string =>
  unicode
    .split('-')
    .map(code => String.fromCodePoint(<any>`0x${code}`))
    .join('');

export const EMOJIS = emoji.reduce(
  (
    acc: EmojiItem[],
    [
      unified,
      shortName,
      shortNames,
      category,
      sortOrder,
      addedIn,
      hasImgTwitter,
      skinVariations,
    ]: EmojiJsonItem,
  ) => {
    const emoji: EmojiItem = {
      unified: unified,
      symbol: unifiedToEmoji(unified),
      shortName: shortName,
      shortNames: shortNames,
      category: categoryMap[category],
      sortOrder: sortOrder,
      addedIn: addedIn,
      hasImgTwitter: hasImgTwitter,
      skinVariations: skinVariations ? <Record<SkinColour, EmojiSkinVariation>>Object.fromEntries(
            Object.entries(skinVariations).map(([key, value]) => [
              key,
              {
                unified: value.unified,
                symbol: unifiedToEmoji(value.unified),
                addedIn: value.addedIn,
                hasImgTwitter: value.hasImgTwitter,
              },
            ]),
          ) : null,
    };

    if (emoji.skinVariations) {
      Object.values(emoji.skinVariations).forEach(variation => (variation.base = emoji));
    }

    return [...acc, emoji];
  },
  <EmojiItem[]>[],
);

export const ALl_EMOJIS: Array<EmojiItem | EmojiSkinVariation> = EMOJIS.flatMap(emoji => [
  emoji,
  ...Object.values(emoji.skinVariations || {}),
]);

export const SYMBOL_EMOJI_MAP = <Record<string, EmojiItem>>(
  Object.fromEntries(ALl_EMOJIS.map(emoji => [emoji.symbol, emoji]))
);

export const EMOJI_SHORTCODE_MAP = <Record<string, EmojiItem>>(
  Object.fromEntries(EMOJIS.map(item => [item.shortName, item]))
);

const SKIP_CATEGORIES = Object.fromEntries([EmojiCategory.SkinTones].map(name => [name, true]));
const CATEGORY_ORDER = Object.fromEntries(
  [
    EmojiCategory.SmileysEmotion,
    EmojiCategory.PeopleBody,
    EmojiCategory.AnimalsNature,
    EmojiCategory.FoodDrink,
    EmojiCategory.TravelPlaces,
    EmojiCategory.Activities,
    EmojiCategory.Objects,
    EmojiCategory.Symbols,
    EmojiCategory.Flags,
  ].map((name, index) => [name, index]),
);
export const FORCE_FALLBACK = ['👩‍❤️‍💋‍👨', '👨‍❤️‍💋‍👨', '👩‍❤️‍💋‍👩', '👩‍❤️‍👨', '👨‍❤️‍👨', '👩‍❤️‍👩'];

export const emojiItemToSymbolItem = (item: EmojiItem): SymbolItem<EmojiItem> => ({
  title: `:${item.shortName}:`,
  searchTerm: item.shortName,
  value: item,
});
export const EMOJI_GROUPED: Array<SymbolGroup<EmojiItem>> = Object.entries(
  EMOJIS.reduce(
    (acc, item) => {
      if (SKIP_CATEGORIES[item.category]) return acc;
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category] = [
        ...acc[item.category],
        { title: `:${item.shortName}:`, searchTerm: item.shortName, value: item },
      ];

      return acc;
    },
    <Record<string, Array<SymbolItem<EmojiItem>>>>{},
  ),
)
  .map(([label, data]) => ({
    label,
    data: [...data].sort((a, b) => (a.value.sortOrder > b.value.sortOrder ? 1 : -1)),
  }))
  .sort((a, b) => (CATEGORY_ORDER[a.label] > CATEGORY_ORDER[b.label] ? 1 : -1));

export const ICONS_GROUPED: Array<SymbolGroup<string>> = [
  {
    label: 'app.emoji.icons',
    data: materialSymbols.map(name => ({
      title: name,
      searchTerm: name,
      value: name,
    })),
  },
];
